import { ABCDCreative } from "@blisspointmedia/bpm-types/dist/ABCD";
import { ABCDCreativeSettings } from "@blisspointmedia/bpm-types/dist/pgTables/ABCDCreativeSettings";
import { ABCDScoreObject, updateABCDCreative, updateABCDScore } from "../../ABCD/ABCDUtils";
import { CreativeDate, EditCreativeMediatypeConfig, FieldConfig } from "../EditCreativeConfigUtils";
import { CreativeLambdaFetch } from "../../../utils/fetch-utils";
import { CreativeMapItem, CreativeTimeline, TimelineKey } from "../../../redux/creative";
import { DateRange } from "@blisspointmedia/bpm-types/dist/RelativeDatePresets";
import { Form } from "react-bootstrap";
import { getCustomAttributes } from "../../AddEditCreativeModal/Configs/TVADAddCreativeConfig";
import { TODAY } from "@blisspointmedia/bpm-types/dist/RelativeDatePicker";
import * as Dfns from "date-fns";
import * as R from "ramda";
import DateInputField from "../../../Components/DatePicker/DateInputField/DateInputField";
import {
  MdAdd,
  MdCancel,
  MdCheckBox,
  MdEdit,
  MdOutlineArrowBackIos,
  MdOutlineArrowForwardIos,
  MdOutlineSquare,
  MdSave,
} from "react-icons/md";
import {
  Button,
  ButtonFrameworkVariant,
  ButtonType,
  Dropdown,
  DropdownToggleType,
  ToggleSwitch,
} from "../../../Components";

const CUSTOM_ATTRIBUTE_KEYWORD = "_CUSTOM_ATTRIBUTE";
const TVAD_MEDIATYPES = ["Linear", "Streaming"];

const convertDateMapToTimeline = dateMap => {
  const timeline = {};
  for (const mediatype of R.keys(dateMap)) {
    timeline[mediatype] = {
      ranges: R.map(
        range => ({
          endDate: range.end,
          startDate: range.start,
        }),
        dateMap[mediatype]
      ),
    };
  }
  return timeline;
};
export const convertTimelineToDateMap = (
  timeline: Record<TimelineKey, CreativeTimeline>
): Record<string, { start: string; end: string }[]> => {
  const dateMap = {};
  for (const mediatype of R.keys(timeline)) {
    dateMap[mediatype] = R.map(
      range => ({
        end: range.endDate,
        start: range.startDate,
      }),
      timeline[mediatype].ranges
    );
    dateMap[mediatype] = R.sortBy(range => range.start, dateMap[mediatype] as { start: string }[]);
  }
  return dateMap;
};

const getCreativeID = (creative: CreativeMapItem): string => creative.isci;

interface GetCreativeChangesArgs {
  abcdCreativeSettings: ABCDCreativeSettings;
  abcdScore: ABCDScoreObject;
  creative: CreativeMapItem;
  initialABCDCreativeSettings: ABCDCreativeSettings;
  initialABCDScore: ABCDScoreObject;
  modifiedCreative: CreativeMapItem;
}
const getCreativeChanges = (
  args: GetCreativeChangesArgs
): {
  checkedAttributeOptions: Record<string, Record<string, boolean>>;
  creativeDifferences: Record<string, any>;
  deletedDates: CreativeDate[];
  newDates: CreativeDate[];
  numChanges: number;
  shouldUpdateABCDScore: boolean;
} => {
  const {
    abcdCreativeSettings,
    abcdScore,
    creative,
    initialABCDCreativeSettings,
    initialABCDScore,
    modifiedCreative,
  } = args;
  const checkedAttributeOptions = {};
  const creativeDifferences: Record<string, any> = {};
  const deletedDates: CreativeDate[] = [];
  const newDates: CreativeDate[] = [];
  let numABCDScoreChanges = 0;
  for (const key of R.keys(creative)) {
    if (key === "media_types") {
      if (!creative[key].includes("linear") && modifiedCreative[key].includes("linear")) {
        creativeDifferences.addLinear = true;
      }
      if (!creative[key].includes("streaming") && modifiedCreative[key].includes("streaming")) {
        creativeDifferences.addStreaming = true;
      }
    } else if (key === "timeline") {
      const initialDatesMap = convertTimelineToDateMap(
        creative[key] as Record<TimelineKey, CreativeTimeline>
      );
      const currentDateMap = convertTimelineToDateMap(
        modifiedCreative[key] as Record<TimelineKey, CreativeTimeline>
      );
      for (const mediatype of R.map(R.toLower, TVAD_MEDIATYPES)) {
        const initialDates = initialDatesMap[mediatype];
        const currentDates = currentDateMap[mediatype];
        // Go through and find which ranges in current are new
        for (const currentDate of currentDates) {
          let found = false;
          for (const initialDate of initialDates) {
            if (initialDate.start === currentDate.start && initialDate.end === currentDate.end) {
              found = true;
              break;
            }
          }
          if (!found && currentDate.start) {
            const newDate = {
              startDate: currentDate.start,
              [mediatype]: true,
            };
            if (currentDate.end) {
              newDate.endDate = currentDate.end;
            }
            newDates.push(newDate);
          }
        }
        // Go through and find which ranges in current have been deleted
        for (const initialDate of initialDates) {
          let found = false;
          for (const currentDate of currentDates) {
            for (const initialDate of initialDates) {
              if (initialDate.start === currentDate.start && initialDate.end === currentDate.end) {
                found = true;
                break;
              }
            }
          }
          if (!found) {
            const deletedDate = {
              startDate: initialDate.start,
              [mediatype]: true,
            };
            if (initialDate.end) {
              deletedDate.endDate = initialDate.end;
            }
            deletedDates.push(deletedDate);
          }
        }
      }
    } else if (key === "creativeTags") {
      for (const creativeTag of R.keys(modifiedCreative.creativeTags)) {
        for (const tag of modifiedCreative.creativeTags[creativeTag]) {
          checkedAttributeOptions[creativeTag] = {
            ...R.defaultTo({}, checkedAttributeOptions[creativeTag]),
            [tag]: true,
          };
        }
      }
      for (const creativeTag of R.keys(creative.creativeTags)) {
        for (const tag of creative.creativeTags[creativeTag]) {
          if (
            !R.isNil(checkedAttributeOptions[creativeTag]) &&
            !R.isNil(checkedAttributeOptions[creativeTag][tag]) &&
            checkedAttributeOptions[creativeTag][tag]
          ) {
            delete checkedAttributeOptions[creativeTag][tag]; // Remove no changes
          } else if (
            R.isNil(checkedAttributeOptions[creativeTag]) ||
            R.isNil(checkedAttributeOptions[creativeTag][tag])
          ) {
            // If we removed an element, add it as false
            checkedAttributeOptions[creativeTag] = {
              ...R.defaultTo({}, checkedAttributeOptions[creativeTag]),
              [tag]: false,
            };
          }
        }
        if (
          checkedAttributeOptions[creativeTag] &&
          R.isEmpty(checkedAttributeOptions[creativeTag])
        ) {
          delete checkedAttributeOptions[creativeTag];
        }
      }
    } else if (key === "clickthroughUrl" && modifiedCreative[key] !== creative[key]) {
      creativeDifferences.clickthrough = modifiedCreative[key];
    } else if (!R.equals(creative[key], modifiedCreative[key])) {
      creativeDifferences[key] = modifiedCreative[key];
    }
  }
  if (abcdScore) {
    for (let i = 0; i < abcdScore.annotations_evaluation.evaluated_features.length; i++) {
      const feature = abcdScore.annotations_evaluation.evaluated_features[i];
      if (
        feature.detected !== initialABCDScore.annotations_evaluation.evaluated_features[i].detected
      ) {
        numABCDScoreChanges++;
      }
    }
  }
  const numChanges =
    R.keys(creativeDifferences).length +
    deletedDates.length +
    newDates.length +
    R.sum(
      R.map(
        checkedAttributeOptionChanges => R.keys(checkedAttributeOptionChanges).length,
        R.values(checkedAttributeOptions)
      )
    ) +
    R.sum(
      R.map(
        key => (R.equals(abcdCreativeSettings[key], initialABCDCreativeSettings[key]) ? 0 : 1),
        R.keys(abcdCreativeSettings)
      )
    ) +
    numABCDScoreChanges;

  if (R.keys(creativeDifferences).length) {
    creativeDifferences.isci = creative.isci;
  }
  return {
    checkedAttributeOptions,
    creativeDifferences,
    deletedDates,
    newDates,
    numChanges,
    shouldUpdateABCDScore: numABCDScoreChanges > 0,
  };
};

interface RenderModalHeaderArgs {
  bulkEditCreatives: CreativeMapItem[];
  modifiedCreative: CreativeMapItem;
  modifyingName: string;
  sections: Record<string, FieldConfig>;
  selectedSection: string;
  setModifiedCreative: Function;
  setModifyingName: Function;
  setSelectedSection: Function;
}

const renderModalHeader = (args: RenderModalHeaderArgs): JSX.Element => {
  const {
    bulkEditCreatives,
    modifiedCreative,
    modifyingName,
    sections,
    selectedSection,
    setModifiedCreative,
    setModifyingName,
    setSelectedSection,
  } = args;
  return (
    <>
      <div className="headerContainer">
        {bulkEditCreatives.length ? (
          <div className="creativeID">Bulk Editing {bulkEditCreatives.length + 1} ISCIs</div>
        ) : (
          <div className="creativeID">{modifiedCreative.isci}</div>
        )}
        <div className="border"></div>
        <div className="creativeName">
          {modifyingName ? (
            <Form.Control
              onChange={e =>
                setModifiedCreative({
                  ...modifiedCreative,
                  name: e.target.value,
                })
              }
              placeholder={"Enter Creative Name:"}
              type="text"
              value={modifiedCreative.name}
            />
          ) : (
            modifiedCreative.name
          )}
        </div>
        <div className="editIcon" onClick={() => setModifyingName(!modifyingName)}>
          {modifyingName ? <MdSave /> : <MdEdit />}
        </div>
      </div>
      <div className="subHeaderContainer">
        {bulkEditCreatives.length ? (
          R.map(creative => <div className="token">{creative.isci}</div>, [
            modifiedCreative,
            ...bulkEditCreatives,
          ])
        ) : (
          <>
            <div className="token">{modifiedCreative.length}s</div>
            <div className="token">{modifiedCreative.language}</div>
            <div className="token">{modifiedCreative.avail === "N" ? "National" : "Local"}</div>
          </>
        )}
      </div>

      <div className="sectionHeaders">
        {R.map(
          sectionLabel => (
            <div
              className={selectedSection === sectionLabel ? "selected" : ""}
              onClick={() => setSelectedSection(sectionLabel)}
            >
              {sectionLabel}
            </div>
          ),
          R.keys(sections)
        )}
      </div>
    </>
  );
};

interface RetireISCIArgs {
  company: string;
  creative: CreativeMapItem;
  onHide: Function;
  setAreYouSure: Function;
  setError: Function;
  setSaving: Function;
}

const retireISCI = async (args: RetireISCIArgs): Promise<void> => {
  const { company, creative, onHide, setError, setAreYouSure, setSaving } = args;
  let confirmSave = false;
  const { isci, name } = creative;
  for (const mediatype of R.map(R.toLower, TVAD_MEDIATYPES)) {
    const ranges = R.path([mediatype, "ranges"], creative.timeline) as {
      startDate: string;
      endDate?: string;
    }[];
    for (const { startDate, endDate } of ranges) {
      if (!endDate || endDate >= TODAY) {
        setError({
          title: "Retiring live ISCI",
          variant: "warning",
          message: `You cannot retire an ISCI that is either live or set to become live in the future. You have selected ISCI: ${isci}, live for ${mediatype} from ${startDate} through ${R.defaultTo(
            "forever",
            endDate
          )}.`,
        });
        return;
      }
    }
  }
  try {
    await setAreYouSure({
      title: "Retire ISCIs?",
      message: `You're about to retire ${name} (${isci}). Are you sure you want to continue?`,
      okayText: "Yes",
      cancelText: "Never mind",
      onOkay: () => {
        confirmSave = true;
      },
    });
  } catch (e) {
    return;
  }
  if (confirmSave) {
    try {
      setSaving(true);
      await CreativeLambdaFetch("/", {
        method: "post",
        body: { company, changes: { isci, retired: true } },
      });
      onHide();
    } catch (e) {
      const reportError = e as Error;
      const message = `Couldn't retire ISCI. ${reportError.message}`;
      setError({ message, reportError });
    } finally {
      setSaving(false);
    }
  }
};

interface GetSectionsArgs {
  abcdScore: ABCDScoreObject;
  company: string;
  creative: CreativeMapItem;
  customAttributes: Record<string, FieldConfig>[];
  expandedMap: Record<string, boolean>;
  modifiedCreative: CreativeMapItem;
  onHide: Function;
  setABCDScore: Function;
  setAreYouSure: Function;
  setError: Function;
  setExpandedMap: Function;
  setModifiedCreative: Function;
  setSaving: Function;
}

const getSections = (args: GetSectionsArgs): Record<string, JSX.Element> => {
  const {
    abcdScore,
    company,
    creative,
    customAttributes,
    expandedMap,
    modifiedCreative,
    onHide,
    setABCDScore,
    setAreYouSure,
    setError,
    setExpandedMap,
    setModifiedCreative,
    setSaving,
  } = args;
  if (R.isNil(modifiedCreative)) {
    return {};
  }
  const initialDatesMap = convertTimelineToDateMap(
    creative.timeline as Record<TimelineKey, CreativeTimeline>
  );
  const currentDateMap: Record<string, object[]> =
    modifiedCreative && modifiedCreative.timeline
      ? convertTimelineToDateMap(modifiedCreative.timeline)
      : {};
  const conceptOptions =
    customAttributes && customAttributes[`${"concept"}`] && customAttributes[`${"concept"}`].options
      ? customAttributes[`${"concept"}`].options
      : [];
  const insightCategoriesOptions =
    customAttributes &&
    customAttributes[`${"insightCategories"}`] &&
    customAttributes[`${"insightCategories"}`].options
      ? customAttributes[`${"insightCategories"}`].options
      : [];
  const sectionScoreMap: Record<
    string,
    {
      presentFeatures: number;
      sectionFeatures: number;
      scoreMap: Record<string, number>;
    }
  > = {};
  if (abcdScore) {
    for (const feature of abcdScore.annotations_evaluation.evaluated_features) {
      const { category, detected, name } = feature;
      const featurePresent = detected ? 1 : 0;
      sectionScoreMap[category] = {
        presentFeatures: sectionScoreMap[category]
          ? sectionScoreMap[category].presentFeatures + featurePresent
          : featurePresent,
        sectionFeatures: sectionScoreMap[category]
          ? sectionScoreMap[category].sectionFeatures + 1
          : 1,
        scoreMap: {
          ...(sectionScoreMap[category] && sectionScoreMap[category].scoreMap
            ? sectionScoreMap[category].scoreMap
            : {}),
          [name]: featurePresent,
        },
      };
    }
  }
  return {
    "Live Dates": (
      <div className="liveDatesSection">
        {R.map(
          mediatype => (
            <>
              <div className="mediatypeContainer">
                <div className="mediatype">{mediatype}</div>
                <div className="firstStartDate">
                  {initialDatesMap[R.toLower(mediatype)].length
                    ? `Started ${Dfns.format(
                        Dfns.parseISO(initialDatesMap[R.toLower(mediatype)][0].start),
                        "MM/dd/yyyy"
                      )}`
                    : `This ISCI isn't live on ${mediatype}`}
                </div>
              </div>
              {(R.defaultTo([], currentDateMap[R.toLower(mediatype)]) as DateRange[]).map(
                (range, index) => (
                  <div className="dateRange">
                    {R.map(
                      label => (
                        <div className="dateContainer">
                          <div className="label">{label}:</div>
                          <div className="date">
                            <DateInputField
                              label={undefined}
                              setValue={value => {
                                currentDateMap[R.toLower(mediatype)][index][
                                  R.toLower(label)
                                ] = value;
                                setModifiedCreative({
                                  ...modifiedCreative,
                                  timeline: convertDateMapToTimeline(currentDateMap),
                                });
                              }}
                              value={range[R.toLower(label)]}
                            />
                          </div>
                        </div>
                      ),
                      ["Start", "End"]
                    )}
                    <div
                      className="deleteIcon"
                      onClick={() => {
                        const modifiedDateMap = R.clone(currentDateMap);
                        const dateRangeToRemove = modifiedDateMap[R.toLower(mediatype)][index] as {
                          start: string;
                          end: string;
                        };
                        modifiedDateMap[R.toLower(mediatype)] = R.remove(
                          index,
                          1,
                          modifiedDateMap[R.toLower(mediatype)]
                        );
                        const media_types: string[] = [];
                        for (const mediatype of R.keys(modifiedDateMap)) {
                          if (modifiedDateMap[R.toLower(mediatype)].length > 0) {
                            media_types.push(mediatype);
                          }
                        }
                        // If a period is live, add a warning
                        if (
                          Dfns.parseISO(dateRangeToRemove.start) <= new Date() &&
                          (R.isNil(dateRangeToRemove.end) ||
                            Dfns.parseISO(dateRangeToRemove.end) >= new Date())
                        ) {
                          setAreYouSure({
                            promise: false,
                            title: "Delete Live Date",
                            message: "You're about to delete a live date, are you sure?",
                            cancelText: "Cancel",
                            okayText: "Continue",
                            onCancel: () => {
                              setModifiedCreative(modifiedCreative);
                            },
                            onOkay: () => {
                              setModifiedCreative({
                                ...modifiedCreative,
                                media_types,
                                timeline: convertDateMapToTimeline(modifiedDateMap),
                              });
                            },
                          });
                        } else {
                          setModifiedCreative({
                            ...modifiedCreative,
                            media_types,
                            timeline: convertDateMapToTimeline(modifiedDateMap),
                          });
                        }
                      }}
                    >
                      <MdCancel />
                    </div>
                  </div>
                )
              )}
              <div className="addDateButtonContainer">
                <Button
                  design="secondary"
                  icon={<MdAdd />}
                  onClick={() => {
                    currentDateMap[R.toLower(mediatype)].push({
                      end: "",
                      start: "",
                    });
                    const media_types: string[] = [];
                    for (const mediatype of R.keys(currentDateMap)) {
                      if (currentDateMap[R.toLower(mediatype)].length > 0) {
                        media_types.push(mediatype);
                      }
                    }
                    setModifiedCreative({
                      ...modifiedCreative,
                      media_types,
                      timeline: convertDateMapToTimeline(currentDateMap),
                    });
                  }}
                  type={ButtonType.FILLED}
                  variant={ButtonFrameworkVariant.LEADING_ICON}
                >
                  {currentDateMap[R.toLower(mediatype)].length
                    ? "Add Live Period"
                    : `Add ${mediatype}`}
                </Button>
              </div>
              {mediatype !== TVAD_MEDIATYPES[TVAD_MEDIATYPES.length - 1] && (
                <div className="border" />
              )}
            </>
          ),
          TVAD_MEDIATYPES
        )}
      </div>
    ),
    ...(modifiedCreative && modifiedCreative.media_types.includes("streaming")
      ? {
          "Clickthrough URL": (
            <div className="clickthroughURLSection">
              <div className="labelContainer">
                <div className="label">Clickthrough URL</div>
                <div className="note">(Streaming)</div>
              </div>
              <Form.Control
                onChange={e =>
                  setModifiedCreative({
                    ...modifiedCreative,
                    clickthroughUrl: e.target.value,
                  })
                }
                placeholder={"Enter Clickthrough URL:"}
                type="text"
                value={modifiedCreative.clickthroughUrl}
              />
            </div>
          ),
        }
      : {}),
    "Creative Attributes": (
      <div className="creativeAttributes">
        <div className="conceptInsightsAlertsContainer">
          <div>
            <div className="label">Concept</div>
            <div className="dropdownContainer">
              <Dropdown
                autoFill
                onChange={value => setModifiedCreative({ ...modifiedCreative, concept: value })}
                options={R.map(
                  option => ({
                    ...option,
                    value: option.label,
                  }),
                  conceptOptions
                )}
                placeholder={"Select Concept"}
                type={DropdownToggleType.OUTLINED}
                value={modifiedCreative.concept}
              />
            </div>
          </div>
          {insightCategoriesOptions.length > 0 && (
            <div>
              <div className="label">Insights Categories</div>
              <div className="dropdownContainer">
                <Dropdown
                  autoFill
                  onChange={value => {
                    const previousCategories =
                      modifiedCreative &&
                      modifiedCreative.creativeTags &&
                      modifiedCreative.creativeTags["Insights Category"]
                        ? modifiedCreative.creativeTags["Insights Category"]
                        : [];
                    setModifiedCreative({
                      ...modifiedCreative,
                      creativeTags: {
                        ...modifiedCreative.creativeTags,
                        "Insights Category": previousCategories.includes(value)
                          ? R.filter(category => category !== value, previousCategories)
                          : R.append(value, previousCategories),
                      },
                    });
                  }}
                  options={insightCategoriesOptions}
                  placeholder={"Select Insight Category"}
                  type={DropdownToggleType.OUTLINED}
                  value={R.join(
                    ", ",
                    modifiedCreative &&
                      modifiedCreative.creativeTags &&
                      modifiedCreative.creativeTags["Insights Category"]
                      ? modifiedCreative.creativeTags["Insights Category"]
                      : []
                  )}
                />
              </div>
            </div>
          )}
          <div className="tagAlertsContainer">
            <ToggleSwitch
              checked={modifiedCreative[`${"suppress_tag_alerts"}`]}
              design="secondary"
              label="Tag Alerts"
              onChange={() =>
                setModifiedCreative({
                  ...modifiedCreative,
                  [`${"suppress_tag_alerts"}`]: !R.defaultTo(
                    false,
                    modifiedCreative[`${"suppress_tag_alerts"}`]
                  ),
                })
              }
              size={"lg"}
            />
          </div>
        </div>
        <div className="border" />
        <div className="customAttributes">
          {R.map(key => {
            if (key.includes(CUSTOM_ATTRIBUTE_KEYWORD)) {
              const creativeTagKey = key.replace(CUSTOM_ATTRIBUTE_KEYWORD, "");
              const customAttribute = customAttributes[key];
              const selectedOptions =
                modifiedCreative.creativeTags && modifiedCreative.creativeTags[creativeTagKey]
                  ? modifiedCreative.creativeTags[creativeTagKey]
                  : [];
              const expanded = R.defaultTo(false, expandedMap[creativeTagKey]);
              return (
                <>
                  <div className={`customAttribute ${expanded ? "expanded" : "collapsed"}`}>
                    <div
                      className="customAttributeTitle"
                      onClick={() =>
                        setExpandedMap({ ...expandedMap, [creativeTagKey]: !expanded })
                      }
                    >
                      <div className="label">{customAttribute.label}</div>
                      <div className={`collapseIcon ${expanded ? "expanded" : "collapsed"}`}>
                        {expanded ? <MdOutlineArrowForwardIos /> : <MdOutlineArrowBackIos />}
                      </div>
                    </div>
                    {expandedMap[creativeTagKey] && (
                      <div className="options">
                        {R.map(
                          option => (
                            <div
                              className="option"
                              onClick={() => {
                                setModifiedCreative({
                                  ...modifiedCreative,
                                  creativeTags: {
                                    ...modifiedCreative.creativeTags,
                                    [creativeTagKey]: selectedOptions.includes(option.value)
                                      ? R.filter(value => value !== option.value, selectedOptions)
                                      : R.append(option.value, selectedOptions).sort(),
                                  },
                                });
                              }}
                            >
                              <div className="checkboxContainer">
                                {selectedOptions && selectedOptions.includes(option.value) ? (
                                  <MdCheckBox />
                                ) : (
                                  <MdOutlineSquare />
                                )}
                              </div>
                              <div className="label">{option.value}</div>
                            </div>
                          ),
                          customAttribute.options
                        )}
                      </div>
                    )}
                  </div>
                  <div className="border" />
                </>
              );
            }
            return <></>;
          }, R.keys(customAttributes) as string[])}
        </div>
      </div>
    ),
    ...(abcdScore
      ? {
          "ABCD Score": (
            <div className="abcdScoreContainer">
              <div className="abcdScore">
                {R.map(sectionName => {
                  const section = sectionScoreMap[sectionName];
                  const { scoreMap } = section;
                  const expanded = R.defaultTo(false, expandedMap[sectionName]);
                  return (
                    <>
                      <div className={`abcdSection ${expanded ? "expanded" : "collapsed"}`}>
                        <div
                          className="abcdSectionTitle"
                          onClick={() =>
                            setExpandedMap({ ...expandedMap, [sectionName]: !expanded })
                          }
                        >
                          <div className="label">{sectionName}</div>
                          <div className={`collapseIcon ${expanded ? "expanded" : "collapsed"}`}>
                            {expanded ? <MdOutlineArrowForwardIos /> : <MdOutlineArrowBackIos />}
                          </div>
                        </div>
                        {expandedMap[sectionName] && (
                          <div className="options">
                            {R.map(
                              featureName => (
                                <div
                                  className="option"
                                  onClick={() => {
                                    const newScore = R.clone(abcdScore);
                                    for (const feature of newScore.annotations_evaluation
                                      .evaluated_features) {
                                      if (feature.name === featureName) {
                                        feature.detected = !feature.detected;
                                      }
                                    }
                                    setABCDScore(newScore);
                                  }}
                                >
                                  <div className="checkboxContainer">
                                    {scoreMap[featureName] === 1 ? (
                                      <MdCheckBox />
                                    ) : (
                                      <MdOutlineSquare />
                                    )}
                                  </div>
                                  <div className="label">{featureName}</div>
                                </div>
                              ),
                              R.keys(scoreMap)
                            )}
                          </div>
                        )}
                      </div>
                      <div className="border" />
                    </>
                  );
                }, R.keys(sectionScoreMap) as string[])}
              </div>
            </div>
          ),
        }
      : {}),
    Advanced: (
      <div className="advancedSection">
        <div className="retireTitle">Retire ISCI</div>
        <div className="retireNote">
          This remove the ISCI from the client's database.
          <br />
          Only do this if you're very sure.
          <br /> This cannot be undone.
        </div>
        <div className="retireButton">
          <Button
            type={ButtonType.FILLED}
            onClick={() =>
              retireISCI({ company, creative, onHide, setError, setAreYouSure, setSaving })
            }
          >
            Retire ISCI
          </Button>
        </div>
      </div>
    ),
  };
};

interface UpdateCreativeArgs {
  abcdCreativeSettings: ABCDCreativeSettings;
  abcdScore: ABCDScoreObject;
  bulkEditCreatives: CreativeMapItem[];
  company: string;
  creative: CreativeMapItem;
  customAttributes: Record<string, FieldConfig>;
  initialABCDCreativeSettings: ABCDCreativeSettings;
  initialABCDScore: ABCDScoreObject;
  modifiedCreative: CreativeMapItem;
  onHide: Function;
  setAreYouSure: Function;
  setError: Function;
  setSaving: Function;
}

const updateCreative = async (args: UpdateCreativeArgs): Promise<void> => {
  const {
    abcdCreativeSettings,
    abcdScore,
    bulkEditCreatives,
    company,
    creative: initialCreative,
    customAttributes,
    initialABCDCreativeSettings,
    initialABCDScore,
    modifiedCreative,
    onHide,
    setAreYouSure,
    setError,
    setSaving,
  } = args;
  const { concept, isci } = modifiedCreative;
  for (const creative of [initialCreative, ...bulkEditCreatives]) {
    const {
      checkedAttributeOptions,
      creativeDifferences,
      deletedDates,
      newDates,
      shouldUpdateABCDScore,
    } = getCreativeChanges({
      abcdCreativeSettings,
      abcdScore,
      creative: initialCreative,
      initialABCDCreativeSettings,
      initialABCDScore,
      modifiedCreative,
    });
    creativeDifferences.isci = creative.isci;
    const changes: Record<string, any>[] =
      creativeDifferences && R.keys(creativeDifferences).length ? [creativeDifferences] : [];
    let selectedConcept: { conceptId?: number; label?: string; value?: string } = {};
    if (
      concept &&
      customAttributes &&
      customAttributes.concept &&
      customAttributes.concept.options
    ) {
      for (const option of customAttributes.concept.options) {
        if (option.value === concept) {
          selectedConcept = {
            conceptId: (option as Record<string, string | number>).conceptId as number,
            label: option.label,
            value: option.value,
          };
          break;
        }
      }
    }
    setSaving(true);
    if (modifiedCreative.name === "") {
      setError({
        title: "No Name",
        message: "You cannot have a blank creative name.",
      });
      setSaving(false);
      return;
    }
    if (
      modifiedCreative.clickthroughUrl &&
      !R.test(/^https:\/\//, modifiedCreative.clickthroughUrl)
    ) {
      setError({
        title: "Click-through HTTPS",
        message: `Click-through URLs must begin with "https://". You entered "${modifiedCreative.clickthroughUrl}"`,
      });
      setSaving(false);
      return;
    }
    if (shouldUpdateABCDScore) {
      try {
        await updateABCDScore(company, creative.isci, abcdScore);
      } catch (e) {
        const reportError = e as Error;
        const message = `Couldn't update abcd score. ${reportError.message}`;
        setError({ message, reportError });
        setSaving(false);
        return;
      }
    }
    const body = {
      changes: R.values(changes),
      checkedAttributeOptions,
      company,
      deletedDates,
      isci,
      newDates,
      selectedConcept,
    };
    try {
      setSaving(true);
      await CreativeLambdaFetch("/", {
        method: "post",
        body,
      });
    } catch (e) {
      const error = e as Error;
      if ((error.message || "").includes("The following creative name is already taken")) {
        try {
          await setAreYouSure({
            title: "Creative Already Defined",
            message: `${error.message} The ISCIs you're currently editing might be grouped together with existing creatives on certain pages. Are you sure you want to continue?`,
            okayText: "Add Them Anyway",
          });
        } catch (e) {
          setSaving(false);
          return;
        }
        try {
          await CreativeLambdaFetch("/", {
            method: "post",
            body: {
              ...body,
              skipNameCheck: true,
            },
          });
        } catch (e) {
          let message = `Failed to edit creatives. ${error.message}`;
          setError({ message, reportError: error });
          setSaving(false);
        }
      } else {
        let message = `Couldn't save creative changes. ${error.message}`;
        setError({ message, reportError: e });
        setSaving(false);
      }
    }
    try {
      setSaving(true);
      await updateABCDCreative(abcdCreativeSettings as ABCDCreative);
    } catch (e) {
      const reportError = e as Error;
      const message = `Failed to edit abcd creative settings. ${reportError.message}`;
      setError({ message, reportError });
      setSaving(false);
    }
  }
  onHide();
  setError({
    variant: "success",
    title: `Updated Creative${bulkEditCreatives ? "s" : ""}!`,
    message: `Successfully updated ${company}${R.map(
      creativeToUpdate => `\n${creativeToUpdate.name} (${creativeToUpdate.isci})`,
      [initialCreative, ...bulkEditCreatives] as CreativeMapItem[]
    )}`,
    onOkay: () => window.location.reload(),
  });
};

export const TVAD_CONFIG: EditCreativeMediatypeConfig = {
  getCreativeChanges,
  getCreativeID,
  getCustomAttributes,
  getSections,
  renderModalHeader,
  updateCreative,
};
